import { useEffect, useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import Badge from '../../components/ui/Badge.jsx'
import Button, { IconButton } from '../../components/ui/Button.jsx'
import { useFeedback } from '../../components/ui/feedback.jsx'
import api from '../../lib/api.js'
import { AdminTable, AdminToolbar, Td } from './AdminTable.jsx'
import CompanyDetailsModal from './CompanyDetailsModal.jsx'
import CompanyFormModal from './CompanyFormModal.jsx'

/* Admin — companies CRUD + details/role management. GET /admin/companies/
 * returns the full dataset, so the client filter searches every company. */
export default function CompaniesTab() {
  const { t } = useTranslation()
  const { confirm, toast } = useFeedback()
  const [all, setAll] = useState([])
  const [loading, setLoading] = useState(true)
  const [q, setQ] = useState('')
  const [details, setDetails] = useState(null) // company id
  const [form, setForm] = useState(null) // {} new | {company}

  const load = useCallback(() => {
    setLoading(true)
    api.get('/admin/companies/').then(({ data }) => setAll(data?.companies || [])).finally(() => setLoading(false))
  }, [])
  useEffect(() => { load() }, [load])

  const deactivate = async (c) => {
    const ok = await confirm({ tone: 'danger', destructive: true, title: t('admin.companies.confirmDeactivate'), message: c.name, confirmLabel: t('admin.companies.deactivate'), cancelLabel: t('common.cancel') })
    if (!ok) return
    try { await api.delete(`/admin/companies/${c.id}`); toast(t('admin.companies.deactivated'), 'success'); load() }
    catch (e) { toast(e?.response?.data?.error || t('admin.errorGeneric'), 'error') }
  }

  const rows = all.filter((c) => {
    const s = q.trim().toLowerCase()
    return !s || (c.name || '').toLowerCase().includes(s) || (c.vat_number || '').toLowerCase().includes(s)
  })

  const columns = [
    { key: 'name', label: t('admin.companies.name') }, { key: 'vat', label: t('admin.companies.vat') },
    { key: 'country', label: t('admin.companies.country') }, { key: 'users', label: t('admin.companies.users'), align: 'right' },
    { key: 'status', label: t('admin.companies.status') }, { key: 'actions', label: t('common.actions'), align: 'right' },
  ]

  return (
    <>
      <AdminToolbar search={q} onSearch={setQ} placeholder={t('admin.companies.search')}
        action={<Button variant="primary" icon="plus" onClick={() => setForm({})}>{t('admin.companies.new')}</Button>} />
      <AdminTable columns={columns} rows={rows} loading={loading} empty={t('admin.companies.empty')}
        renderRow={(c) => (
          <>
            <Td strong>{c.name}</Td>
            <Td>{c.vat_number || '—'}</Td>
            <Td>{c.country_code || '—'}</Td>
            <Td align="right">{c.user_count ?? 0}</Td>
            <Td><Badge tone={c.is_active ? 'success' : 'neutral'}>{c.is_active ? t('admin.active') : t('admin.inactive')}</Badge>{!c.setup_completed && <span style={{ marginLeft: 6 }}><Badge tone="warning">{t('admin.companies.setupPending')}</Badge></span>}</Td>
            <Td align="right"><div style={{ display: 'flex', gap: 7, justifyContent: 'flex-end' }}>
              <IconButton icon="eye" title={t('admin.companies.details')} onClick={() => setDetails(c.id)} />
              <IconButton icon="pencil" variant="primary" title={t('common.edit')} onClick={() => setForm({ company: c })} />
              {c.is_active && <IconButton icon="power" variant="danger" title={t('admin.companies.deactivate')} onClick={() => deactivate(c)} />}
            </div></Td>
          </>
        )} />
      {details && <CompanyDetailsModal companyId={details} onClose={() => { setDetails(null); load() }} />}
      {form && <CompanyFormModal company={form.company} onClose={() => setForm(null)} onSaved={() => { setForm(null); toast(t('admin.companies.saved'), 'success'); load() }} />}
    </>
  )
}
